/**
 * The custom lawn plan builder (owner, Oct 22 2026: "I did a custom treatment plan for Scott at Derby Ln, can you
 * make a feature to make that easier").
 *
 * Start from Simple / The Plan / Estate, tick what to add with a count, tap the month each one goes down in, and
 * watch the price and the year move. One tap enrolls: the program, its visits in the right months riding the mows,
 * the crew notes, month-end billing — the same as any plan. The same sheet changes a running plan later (add or
 * drop an extra, move a month) without rebuilding by hand; visits done, started or dated by hand stay put.
 *
 * Every price on this screen is worked out by the server off the Plan Cost Sheet. Nothing here invents a number.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Minus, Plus } from 'lucide-react';
import { api } from '../services/api';

const PLANS = [
  { key: 'full', name: 'Estate', hint: 'the complete year' },
  { key: 'plan', name: 'The Plan', hint: 'most yards' },
  { key: 'simple', name: 'Simple', hint: 'feed and prevent' },
];
const LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const monthLabel = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${SHORT[m - 1]} ${y}`; };
const addMonths = (ym: string, n: number) => { const [y, m] = ym.split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return d.toISOString().slice(0, 7); };

type Props = {
  /** A new plan on this yard … */
  propertyId?: number; customerId?: number;
  /** … or a running plan to change. */
  programId?: number;
  onDone?: () => void; onCancel?: () => void;
};

export default function CustomPlanBuilder({ propertyId, customerId, programId, onDone, onCancel }: Props) {
  const qc = useQueryClient();
  const editing = !!programId;
  const { data: menu, isLoading, error: loadError } = useQuery(
    ['custom-plan-start', programId || null, propertyId || null],
    () => (editing ? api.get(`/plan-sheet/programs/${programId}/custom`) : api.get('/plan-sheet/custom/menu', { params: { property_id: propertyId } })).then(r => r.data),
    { staleTime: 60_000 },
  );

  const [plan, setPlan] = useState('simple');
  const [grass, setGrass] = useState('');
  const [sqft, setSqft] = useState('');
  const [mow, setMow] = useState<boolean | null>(null);
  const [items, setItems] = useState<Record<string, number>>({});
  const [months, setMonths] = useState<Record<string, number[]>>({});
  const [start, setStart] = useState('');
  const [monthly, setMonthly] = useState('');
  const [look, setLook] = useState<any>(null);
  const [looking, setLooking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // The starting point: the yard's own facts and the plan's own counts (or the running plan's mix).
  useEffect(() => {
    if (!menu || ready) return;
    if (editing) {
      const b = menu.built || {};
      setPlan(b.plan || 'simple'); setGrass(b.grass || ''); setSqft(b.sqft ? String(b.sqft) : ''); setMow(b.mow !== false);
      setItems({ ...(b.items || {}) }); setMonths({ ...(b.months || {}) });
      // A price he typed stays typed; a plan on the sheet's own price keeps following the sheet as the mix changes.
      const paid = Math.round(Number(menu.program?.monthly_price) || 0);
      setMonthly(paid && paid !== Number(b.monthly) ? String(paid) : '');
      setStart(menu.program?.start || '');
    } else {
      const first = (menu.plans || []).find((p: any) => p.plan === 'simple') || menu.plans?.[0];
      setPlan(first?.plan || 'simple'); setItems({ ...(first?.items || {}) }); setMonths({});
      setGrass(menu.yard?.grass || ''); setSqft(menu.yard?.sqft ? String(menu.yard.sqft) : ''); setMow(menu.yard ? !!menu.yard.mowed : true);
      setStart(menu.start || '');
    }
    setReady(true);
  }, [menu, editing, ready]);

  // Every change re-prices on the server, which also lays the year out and books the dates (a look, nothing written).
  const payload = JSON.stringify({ plan, grass, sqft, mow, items, months, start, monthly });
  const timer = useRef<any>(null);
  useEffect(() => {
    if (!ready) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLooking(true);
      try {
        const body = editing
          ? { items, months, monthly_price: Number(monthly) > 0 ? Number(monthly) : undefined, dry_run: true }
          : { property_id: propertyId, plan, grass: grass || undefined, sqft: Number(sqft) > 0 ? Number(sqft) : undefined, mow: mow === null ? undefined : mow,
              items, months, start: start || undefined, monthly_price: Number(monthly) > 0 ? Number(monthly) : undefined, dry_run: true };
        const { data } = editing ? await api.put(`/plan-sheet/programs/${programId}/custom`, body) : await api.post('/plan-sheet/custom', body);
        setLook(data); setProblem(null);
        // The server says where each extra actually landed; the screen mirrors it so a "+" shows its month at once.
        const b = data.built || {};
        if (b.items && JSON.stringify(b.items) !== JSON.stringify(items)) setItems({ ...b.items });
        if (b.months && JSON.stringify(b.months) !== JSON.stringify(months)) setMonths({ ...b.months });
      } catch (e: any) {
        setProblem(e?.response?.data?.message || 'Could not work that out');
      } finally { setLooking(false); }
    }, 350);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, ready]);

  const save = useMutation(async () => {
    const body = editing
      ? { items, months, monthly_price: Number(monthly) > 0 ? Number(monthly) : undefined }
      : { property_id: propertyId, plan, grass: grass || undefined, sqft: Number(sqft) > 0 ? Number(sqft) : undefined, mow: mow === null ? undefined : mow,
          items, months, start: start || undefined, monthly_price: Number(monthly) > 0 ? Number(monthly) : undefined };
    return (editing ? api.put(`/plan-sheet/programs/${programId}/custom`, body) : api.post('/plan-sheet/custom', body)).then(r => r.data);
  }, {
    onSuccess: (d: any) => {
      toast.success(d?.message || (editing ? 'Plan changed' : 'On the plan'), { autoClose: 10000 });
      if (customerId) qc.invalidateQueries(['customer-plan-options', customerId]);
      qc.invalidateQueries('treatment-programs'); qc.invalidateQueries('treatment-overview');
      if (programId) qc.invalidateQueries(['treatment-program', programId]);
      onDone?.();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || (editing ? 'Could not change that plan' : 'Could not start that plan')); },
  });

  const built = look?.built;
  const extras: any[] = built?.extras || [];
  const monthsFor = (service: string) => months[service] || built?.months?.[service] || [];
  const step = (service: string, by: number) => {
    const n = Math.max(0, Math.min(12, (items[service] || 0) + by));
    setItems(prev => ({ ...prev, [service]: n }));
    const have = monthsFor(service);
    if (have.length > n) setMonths(prev => ({ ...prev, [service]: have.slice(0, n) }));
  };
  const toggleMonth = (service: string, i: number) => {
    const have = monthsFor(service);
    const next = have.includes(i) ? have.filter(x => x !== i) : [...have, i].sort((a, b) => a - b);
    setMonths(prev => ({ ...prev, [service]: next }));
    setItems(prev => ({ ...prev, [service]: next.length }));
  };
  const choosePlan = (key: string) => {
    const p = (menu?.plans || []).find((x: any) => x.plan === key);
    setPlan(key); setItems({ ...(p?.items || {}) }); setMonths({}); setMonthly('');
  };

  if (isLoading) return <p className="text-xs text-gray-500 mt-2">Loading the builder…</p>;
  if (loadError || !menu) return <p className="text-xs text-red-700 mt-2">{(loadError as any)?.response?.data?.message || 'Could not load the builder'}</p>;

  const needGrass = !editing && !menu.grass_known;
  const needSqft = !editing && !menu.sqft_known;
  const inPlan = extras.filter(e => e.count > 0);
  const rest = extras.filter(e => !e.count);
  const sheetMonthly = built?.monthly;
  const startChoices = !editing && menu.today ? Array.from({ length: 15 }, (_, n) => addMonths(String(menu.today).slice(0, 7), n)) : [];
  if (!editing && start && !startChoices.includes(start)) startChoices.push(start);
  const visits: any[] = look?.preview?.visits || [];

  return (
    <div className="mt-2 rounded-lg border border-emerald-300 bg-emerald-50/60 p-2.5 space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-bold text-emerald-900">{editing ? `Change ${menu.program?.name || 'this plan'}` : 'Build a custom plan'}</p>
          <p className="text-[11px] text-gray-600">{editing ? 'Add or drop anything, move a month. Visits done, started or dated by hand stay as they are.' : 'Start from a plan, add what they want, pick the month. The price follows the cost sheet.'}</p>
        </div>
        {onCancel && <button type="button" onClick={onCancel} className="text-xs text-gray-500">Cancel</button>}
      </div>

      {!editing && (
        <div className="flex flex-wrap gap-1.5">
          {PLANS.map(p => (
            <button key={p.key} type="button" onClick={() => choosePlan(p.key)} title={p.hint}
              className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold ${plan === p.key ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300'}`}>
              {p.name}{(menu.plans || []).find((x: any) => x.plan === p.key) ? <span className="font-normal opacity-80"> ${(menu.plans || []).find((x: any) => x.plan === p.key).monthly}</span> : null}
            </button>
          ))}
        </div>
      )}

      {(needGrass || needSqft || !editing) && (
        <div className="grid grid-cols-2 gap-2">
          {needGrass && (
            <label className="text-[11px] text-gray-600">Grass <span className="text-gray-500">(different year for each)</span>
              <select className="input mt-0.5 py-1.5" value={grass} onChange={e => setGrass(e.target.value)}>
                <option value="">Pick one…</option><option value="bz">Bermuda / Zoysia</option><option value="sa">St. Augustine</option>
              </select></label>
          )}
          {needSqft && (
            <label className="text-[11px] text-gray-600">Lawn they pay for (sq ft)
              <input type="number" inputMode="numeric" className="input mt-0.5 py-1.5" value={sqft} onChange={e => setSqft(e.target.value)} placeholder="sq ft of grass" /></label>
          )}
          {!editing && (
            <label className="text-[11px] text-gray-600">Starts
              <select className="input mt-0.5 py-1.5" value={start} onChange={e => setStart(e.target.value)}>
                {startChoices.map(ym => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
              </select></label>
          )}
          {!editing && mow !== null && (
            <label className="text-[11px] text-gray-600 flex items-end gap-1.5 pb-2">
              <input type="checkbox" checked={mow === false} onChange={e => setMow(!e.target.checked)} /> We don't mow it — treatments only (1.5×)
            </label>
          )}
        </div>
      )}

      {/* The price, as the sheet works it out, and the one he quoted if it differs. */}
      <div className="rounded-lg bg-white border border-emerald-200 p-2 flex items-start justify-between gap-2 flex-wrap">
        <div className="text-xs text-gray-800">
          <p className="font-semibold">{look?.words || built?.plan_name || '…'}</p>
          <p className="text-gray-600">
            {built ? <>{built.grass_name} · {Number(built.sqft).toLocaleString()} sq ft · {built.visits} applications a year{built.mow === false ? ' · treatments only' : ''}</> : looking ? 'Working it out…' : ''}
            {problem && <span className="text-red-700"> {problem}</span>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-gray-900">{sheetMonthly != null ? `$${Number(monthly) > 0 ? Math.round(Number(monthly)) : sheetMonthly}` : '—'}<span className="text-xs font-normal text-gray-500"> / mo</span></p>
          {sheetMonthly != null && Number(monthly) > 0 && Math.round(Number(monthly)) !== sheetMonthly && <p className="text-[11px] text-gray-500">the sheet says ${sheetMonthly}</p>}
          {built && built.mow !== false && <p className="text-[11px] text-gray-500">{built.plan_name} alone is ${built.base_monthly}</p>}
        </div>
      </div>
      <label className="text-[11px] text-gray-600 block">What they pay a month ($) <span className="text-gray-500">— blank is the sheet's price; type it when you already quoted one</span>
        <input type="number" inputMode="decimal" className="input mt-0.5 py-1.5 w-40" value={monthly} onChange={e => setMonthly(e.target.value)} placeholder={sheetMonthly != null ? String(sheetMonthly) : ''} /></label>

      {/* What is in it: count and months per service. */}
      {built && (
        <div className="rounded-lg bg-white border border-emerald-200 divide-y divide-gray-100">
          {inPlan.map(e => <Row key={e.service} e={e} months={monthsFor(e.service)} onStep={step} onMonth={toggleMonth} />)}
          {rest.length > 0 && (
            <details className="px-2 py-1.5">
              <summary className="text-xs font-semibold text-gray-700 cursor-pointer">Add something else</summary>
              <div className="divide-y divide-gray-100">{rest.map(e => <Row key={e.service} e={e} months={[]} onStep={step} onMonth={toggleMonth} />)}</div>
            </details>
          )}
        </div>
      )}

      {/* The year, month by month. */}
      {built && (
        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1">
          {built.year.map((y: any) => (
            <div key={y.index} className={`rounded-md border px-1 py-1 text-center ${y.keys.length ? (y.changed ? 'border-emerald-500 bg-emerald-100' : 'border-emerald-200 bg-white') : 'border-gray-200 bg-gray-50'}`} title={y.name}>
              <p className="text-[10px] font-bold text-gray-700">{SHORT[y.index]}</p>
              <p className="text-[10px] leading-tight text-gray-700 break-words">{y.keys.length ? y.words : '—'}</p>
            </div>
          ))}
        </div>
      )}

      {/* The dates, as booked: each rides the mow that month where there is one. */}
      {!editing && visits.length > 0 && (
        <p className="text-[11px] text-gray-700">
          {visits.length} visit{visits.length === 1 ? '' : 's'} from {start ? monthLabel(start) : 'the start'}, {visits.filter((v: any) => v.rides).length} riding a mow: {visits.map((v: any) => `${day(v.date)} ${v.words.toLowerCase()}`).join(' · ')}
          {look?.preview?.old_schedule_words && <span className="block text-amber-800">{look.preview.old_schedule_words}</span>}
        </p>
      )}
      {editing && look && <p className="text-[11px] text-gray-700">{look.booked} visit{look.booked === 1 ? '' : 's'} would be laid out again from {monthLabel(look.from)} ({look.removed} not-yet-done visit{look.removed === 1 ? '' : 's'} replaced). Done, started and hand-dated visits stay.</p>}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => save.mutate()} disabled={save.isLoading || looking || !built || !!problem}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
          {save.isLoading ? (editing ? 'Saving…' : 'Enrolling…') : editing ? 'Save the change' : `Enroll at $${Number(monthly) > 0 ? Math.round(Number(monthly)) : (sheetMonthly ?? '…')}/mo`}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="text-xs text-gray-500">Cancel</button>}
        <span className="text-[11px] text-gray-500">Nothing is texted or charged. Each treatment rides their mow as one stop; the crew see what goes down, never the price.</span>
      </div>
    </div>
  );
}

function Row({ e, months, onStep, onMonth }: { e: any; months: number[]; onStep: (s: string, by: number) => void; onMonth: (s: string, i: number) => void }) {
  return (
    <div className="px-2 py-1.5">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold capitalize ${e.count ? 'text-gray-900' : 'text-gray-500'}`}>{e.word}{e.in_plan ? <span className="ml-1 font-normal text-[10px] text-emerald-700 normal-case">in {e.base === e.count ? 'the plan' : `the plan ×${e.base}`}</span> : null}</p>
          <p className="text-[11px] text-gray-500">{e.help} <span className="text-gray-400">· ${e.per_visit} a visit{e.delta_year ? ` · ${e.delta_year > 0 ? '+' : '−'}$${Math.abs(e.delta_year)} a year` : ''}</span></p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button type="button" onClick={() => onStep(e.service, -1)} disabled={!e.count} aria-label={`One fewer ${e.word}`}
            className="h-7 w-7 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
          <span className="w-5 text-center text-sm font-semibold text-gray-900">{e.count}</span>
          <button type="button" onClick={() => onStep(e.service, 1)} disabled={e.count >= 12} aria-label={`One more ${e.word}`}
            className="h-7 w-7 rounded-full border border-emerald-400 text-emerald-700 flex items-center justify-center disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {/* Which months: tap to put one there or take it out. */}
      <div className="mt-1 flex gap-0.5">
        {LETTERS.map((l, i) => {
          const on = months.includes(i);
          return (
            <button key={i} type="button" onClick={() => onMonth(e.service, i)} title={SHORT[i]} aria-label={`${e.word} in ${SHORT[i]}`} aria-pressed={on}
              className={`h-6 flex-1 rounded text-[10px] font-bold ${on ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{l}</button>
          );
        })}
      </div>
    </div>
  );
}
